import React from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Msg #987 — Admin Shadow Whisper MONITOR (read-only viewer).
// Red-themed live feed of ALL whispers/PMs across the server. Toggle between:
//   • "Global" — every recent whisper server-wide
//   • "Focused" — enter a username to filter to whispers TO or FROM that user
export default function ShadowWhisperMonitorModal({ open, onClose, requester }) {
  const [mode, setMode] = React.useState('global'); // 'global' | 'focus'
  const [focusName, setFocusName] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [rows, setRows] = React.useState([]);
  const tailRef = React.useRef(null);
  // Msg #991 — Auto-scroll only on the FIRST fetch of a session/mode change.
  // After that, respect the admin's scroll position so the feed doesn't jump
  // to the bottom every 6s auto-refresh.
  const didFirstScrollRef = React.useRef(false);

  const fetchFeed = React.useCallback(async () => {
    setErr('');
    setBusy(true);
    try {
      const params = { requester, limit: 200 };
      if (mode === 'focus' && focusName.trim()) {
        params.user = focusName.trim();
      }
      const r = await axios.get(`${API}/admin/whispers/monitor`, { params });
      setRows(r.data?.whispers || []);
      if (!didFirstScrollRef.current) {
        setTimeout(() => {
          if (tailRef.current) tailRef.current.scrollTop = tailRef.current.scrollHeight;
        }, 30);
        didFirstScrollRef.current = true;
      }
    } catch (e) {
      const d = e && e.response && e.response.data && e.response.data.detail;
      setErr(d || 'Failed to fetch whisper feed.');
    } finally { setBusy(false); }
  }, [requester, mode, focusName]);

  React.useEffect(() => {
    if (!open) return;
    didFirstScrollRef.current = false;  // Reset when modal opens/mode changes
    fetchFeed();
    // Msg #1001 — Bumped from 6 s → 15 s. Moderation feed doesn't need
    // sub-10s freshness.
    const iv = setInterval(fetchFeed, 15000);
    return () => clearInterval(iv);
  }, [open, fetchFeed]);

  if (!open) return null;

  const fmtTs = (ts) => {
    try {
      const d = new Date(ts);
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch { return ''; }
  };

  return (
    <div
      data-testid="shadow-whisper-monitor"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1200,
        background: 'rgba(30,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(96vw, 780px)',
          maxHeight: '86vh',
          display: 'flex', flexDirection: 'column',
          background: '#2a0a0a',
          border: '3px double #7a1a1a',
          color: '#fce2d0',
          fontFamily: 'Arial, Helvetica, sans-serif',
          boxShadow: '0 8px 26px rgba(0,0,0,0.6)',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 14px', background: '#5a0a0a', color: '#ffe1dc',
          borderBottom: '1px solid #7a1a1a',
        }}>
          <div style={{ fontWeight: 'bold', letterSpacing: 2, textTransform: 'uppercase', fontSize: 14 }}>
            🥷 Shadow Whisper Monitor
          </div>
          <button
            onClick={onClose}
            data-testid="shadow-whisper-monitor-close"
            style={{ background: 'transparent', color: '#ffe1dc',
                     border: '1px solid #ffe1dc', padding: '2px 10px',
                     fontWeight: 'bold', cursor: 'pointer' }}
          >✕</button>
        </div>

        {/* Controls */}
        <div style={{ padding: '10px 14px', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 4 }}>
            <button
              data-testid="shadow-monitor-mode-global"
              onClick={() => setMode('global')}
              style={{
                background: mode === 'global' ? '#a02020' : '#1a0505',
                color: '#fce2d0',
                border: '1px solid #7a1a1a',
                padding: '3px 12px', fontSize: 11, fontWeight: 'bold', letterSpacing: 1,
                cursor: 'pointer',
              }}
            >GLOBAL FEED</button>
            <button
              data-testid="shadow-monitor-mode-focus"
              onClick={() => setMode('focus')}
              style={{
                background: mode === 'focus' ? '#a02020' : '#1a0505',
                color: '#fce2d0',
                border: '1px solid #7a1a1a',
                padding: '3px 12px', fontSize: 11, fontWeight: 'bold', letterSpacing: 1,
                cursor: 'pointer',
              }}
            >FOCUS USER</button>
          </div>
          {mode === 'focus' && (
            <input
              data-testid="shadow-monitor-focus-input"
              value={focusName}
              onChange={(e) => setFocusName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchFeed()}
              placeholder="username to focus on"
              style={{
                background: '#1a0505', color: '#fce2d0',
                border: '1px solid #7a1a1a',
                padding: '3px 8px', fontSize: 12,
                fontFamily: 'inherit', minWidth: 180,
              }}
            />
          )}
          <button
            data-testid="shadow-monitor-refresh"
            onClick={fetchFeed}
            disabled={busy}
            style={{
              background: '#7a1a1a', color: '#fff3e0',
              border: '1px solid #ff6060', padding: '3px 12px',
              fontSize: 11, fontWeight: 'bold', letterSpacing: 1,
              cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1,
            }}
          >{busy ? 'Loading…' : 'REFRESH'}</button>
          <span style={{ fontSize: 11, opacity: 0.8, marginLeft: 'auto' }}>
            {rows.length} {rows.length === 1 ? 'whisper' : 'whispers'} · auto-refresh 6s
          </span>
        </div>

        {err && (
          <div data-testid="shadow-monitor-error"
               style={{ color: '#ff9a9a', fontSize: 12, padding: '0 14px 6px' }}>{err}</div>
        )}

        {/* Feed */}
        <div
          ref={tailRef}
          data-testid="shadow-monitor-feed"
          style={{
            flex: 1, minHeight: 200, overflowY: 'auto',
            background: '#1a0303',
            padding: '8px 14px',
            fontSize: 12, lineHeight: 1.45,
            fontFamily: 'ui-monospace, Menlo, monospace',
          }}
        >
          {rows.length === 0 ? (
            <div style={{ opacity: 0.6, textAlign: 'center', padding: '30px 0' }}>
              {mode === 'focus' && !focusName.trim()
                ? 'Enter a username above to focus the feed.'
                : 'No whispers to display.'}
            </div>
          ) : rows.map((w) => (
            <div
              key={w.id}
              data-testid={`shadow-monitor-row-${w.id}`}
              style={{
                marginBottom: 4,
                paddingBottom: 3,
                borderBottom: '1px dashed rgba(255,255,255,0.06)',
              }}
            >
              <span style={{ color: '#a0a0a0', marginRight: 6 }}>[{fmtTs(w.ts)}]</span>
              <b style={{ color: '#ff9a9a' }}>{w.from_name}</b>
              <span style={{ margin: '0 6px', color: '#7a1a1a' }}>→</span>
              <b style={{ color: '#ffd28a' }}>{w.to_name}</b>
              {w.is_shadow && (
                <span style={{
                  marginLeft: 6, padding: '0 4px',
                  background: '#a02020', color: '#fff3e0',
                  fontSize: 9, fontWeight: 'bold', letterSpacing: 1,
                  borderRadius: 6,
                }}>SHADOW</span>
              )}
              {w.via === 'pm' && (
                <span style={{
                  marginLeft: 6, padding: '0 4px',
                  background: '#1a4a1a', color: '#dff5c0',
                  fontSize: 9, fontWeight: 'bold', letterSpacing: 1,
                  borderRadius: 6,
                }}>PM</span>
              )}
              {w.undeliverable && (
                <span style={{
                  marginLeft: 6, padding: '0 4px',
                  background: '#3a3a3a', color: '#cccccc',
                  fontSize: 9, fontWeight: 'bold', letterSpacing: 1,
                  borderRadius: 6,
                }}>UNDELIVERED</span>
              )}
              <span style={{ color: '#fce2d0', marginLeft: 8, whiteSpace: 'pre-wrap' }}>{w.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
